import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { DisplayableError } from '@meterup/common';
import { ClientBadRequestError, ResourceNotFoundError, UnexpectedError } from '@meterup/common';
import { type GraphQLError, Kind } from 'graphql';
import { ClientError } from 'graphql-request';

/*
 * Known extension error codes that we support, will be defined from backend.
 */
export enum GraphQLExtensionErrorCode {
  AlreadyExists = 'ALREADY_EXISTS',
  BadRequest = 'BAD_REQUEST',
  InternalServerError = 'INTERNAL_SERVER_ERROR',
  NotFound = 'NOT_FOUND',
  Unauthorized = 'UNAUTHORIZED',
  ValidationError = 'VALIDATION',
}

export { ClientError };

/**
 * Shown in place of raw GraphQL error messages, which can leak internal details (schema fields,
 * type names) to customers. The original error is preserved on the wrapping error's
 * `originalError`/`cause` for Sentry and debugging.
 */
export const GENERIC_UNEXPECTED_ERROR_MESSAGE =
  'An unexpected error occurred. Please try again, and if the issue persists, contact support.';

export function getGraphQLError(originalError: Error): GraphQLError | undefined {
  let error = originalError;
  if (!(error instanceof ClientError) && error?.cause != null && error.cause instanceof Error) {
    error = error.cause;
  }
  if (!(error instanceof ClientError)) return undefined;

  return error.response.errors?.[0];
}

export function getExtensionErrorCode(
  error: GraphQLError | undefined,
): GraphQLExtensionErrorCode | unknown {
  return error?.extensions?.code;
}

export function clientErrorToDisplayableError(clientError: ClientError): DisplayableError {
  const graphqlError = getGraphQLError(clientError);

  switch (graphqlError?.extensions?.code) {
    case GraphQLExtensionErrorCode.NotFound:
    case GraphQLExtensionErrorCode.Unauthorized:
      return new ResourceNotFoundError('Sorry, we were unable to find this resource.', clientError);
    case GraphQLExtensionErrorCode.AlreadyExists:
    case GraphQLExtensionErrorCode.BadRequest:
    case GraphQLExtensionErrorCode.ValidationError:
      return new ClientBadRequestError(graphqlError.message, clientError);
  }

  return new UnexpectedError(GENERIC_UNEXPECTED_ERROR_MESSAGE, clientError);
}

export function getDocumentName(document: TypedDocumentNode<any, any>): string | undefined {
  for (const def of document.definitions) {
    if (def.kind === Kind.OPERATION_DEFINITION) return def.name?.value;
  }

  return undefined;
}

interface DebugQueryParamsOptions {
  readonly operationName: string | undefined;
  /**
   * The client library label (for distinguishing requests during migration)
   *
   * @todo Remove once the Apollo Client migration is complete
   */
  readonly client: 'TanStack' | 'Apollo';
}

/**
 * Add query parameters to the request URL to differentiate queries in the browser developer tools
 * network panel for debugging purposes. This makes it possible to filter to a specific operation.
 *
 * @param url — The base request URL
 * @param options — Parameters to include as query parameter
 *
 * @returns The URL with debug query parameters appended in local
 * environments, or the original URL unchanged in other environments
 */
export const appendDebugQueryParams = (
  url: string,
  { operationName, client }: DebugQueryParamsOptions,
): string => {
  if (import.meta.env.REALM !== 'local' || !operationName) return url;

  const params = new URLSearchParams({ op: `${operationName}::${client}` });
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}${params}`;
};

export function getDocumentActions(document: TypedDocumentNode<any, any>): string[] | undefined {
  for (const def of document.definitions) {
    if (def.kind === Kind.OPERATION_DEFINITION) {
      return def.selectionSet.selections
        .filter(
          (selection): selection is typeof selection & { kind: Kind.FIELD } =>
            selection.kind === Kind.FIELD,
        )
        .map((selection) => selection.name.value);
    }
  }

  return undefined;
}
